import { FC, useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  ButtonBase,
  Card,
  CardContent,
  CardHeader,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import {
  AddCircle,
  MoreVert as MoreVertIcon,
  ChevronLeft as ChevronLeftIcon,
  CloseRounded
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useQueryClient, useMutation } from "@tanstack/react-query";

import { Breadcrumbs } from "components/Breadcrumbs";
import { AppTitleContext } from "app_context";
import useDumbPageTitle from "hooks/useDumbPageTitle";
import { useTrackPageView } from "hooks/useTrackPageView";
import {
  useKpisProgressQuery,
  useKpisCategoriesQuery,
  useKpiYearsUniqueQuery
} from "hooks/queries/kpis";
import { KpisLinearProgress } from "pages/Kpis/KpisLinearProgress";
import { ButtonMenu } from "pages/Kpis/ButtonMenu";
import { AxiosContext } from "contexts/AxiosContext";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { API_URL_KPIS } from "constants/api_urls";
import { ActivityType, Category, KpiType } from "interfaces/kpis";
import { CreateAnnualKpiDialog } from "pages/Kpis/KpisDialogs/CreateAnnualKpiDialog";
import { ConfirmationDialog } from "components/ConfirmationDialog";
import { EditAnnualKpiDialog } from "pages/Kpis/KpisDialogs/EditAnnualKpiDialog";
import { ListCategoriesDialog } from "pages/Kpis/CategoryDialogs/ListCategoriesDialog";
import { LinkButton } from "components/LinkButton";
import { Link } from "react-router-dom";

const PAGE_TITLE = "KPIs";

export const KpisManagePage: FC = (): JSX.Element => {
  useDumbPageTitle(PAGE_TITLE);
  useTrackPageView(PAGE_TITLE);
  const { axios } = useContext(AxiosContext)!;
  const appTitleContext = useContext(AppTitleContext);
  const queryClient = useQueryClient();
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);

  const [showCreateAnnualKpiDialog, setShowCreateAnnualKpiDialog] = useState(
    false
  );
  const [showEditAnnualKpiDialog, setShowEditAnnualKpiDialog] = useState(false);
  const [showDeleteAnnualKpiDialog, setShowDeleteAnnualKpiDialog] = useState(
    false
  );
  const [annualKpiToDelete, setAnnualKpiToDelete] = useState<{
    id: number;
    kpiType: KpiType;
    activityType: ActivityType;
  } | null>(null);
  const [annualKpiToEdit, setAnnualKpiToEdit] = useState<{
    id: number;
    kpiType: KpiType;
    activityType: ActivityType;
  } | null>(null);
  const [showCategoriesDialog, setShowCategoriesDialog] = useState(false);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  useEffect(() => {
    appTitleContext.setPageTitle(
      <Breadcrumbs>
        <Link to={`/pages/kpis`}>KPIs</Link>
        <span>Manage</span>
      </Breadcrumbs>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    data: annualKpis,
    isLoading: isKpisLoading,
    isError: isKpisError
  } = useKpisProgressQuery({
    page: 1,
    size: 999_999,
    category_id: String(selectedCategory) || "",
    year: selectedYear
  });

  const {
    data: yearsUnique,
    isLoading: isYearsUniqueLoading,
    isError: isYearsUniqueError
  } = useKpiYearsUniqueQuery();

  const {
    data: categories,
    isLoading: isCategoriesLoading,
    isError: isCategoriesError
  } = useKpisCategoriesQuery({ page: 1, size: 999_999 });

  const isLoaded = !isKpisLoading && !isCategoriesLoading;

  const deleteAnnualKpiMutation = useMutation({
    mutationFn: async ({
      id,
      kpiType,
      activityType
    }: {
      id: number;
      kpiType: KpiType;
      activityType: ActivityType;
    }): Promise<void> => {
      let endpointUrl = `${API_URL_KPIS}/fieldpro_inspection_kpis/${id}`;
      if (kpiType === "field_pro") {
        if (activityType === "maintenance") {
          endpointUrl = `${API_URL_KPIS}/maintenance_kpis/${id}`;
        } else if (activityType === "inspection") {
          endpointUrl = `${API_URL_KPIS}/fieldpro_inspection_kpis/${id}`;
        }
      }

      if (kpiType === "fleet_monitor") {
        endpointUrl = `${API_URL_KPIS}/fleetmonitor_measurement_kpis/${id}`;
      }

      return axios.delete(endpointUrl);
    },
    onSuccess: async (): Promise<void> => {
      setShowDeleteAnnualKpiDialog(false);
      await queryClient.invalidateQueries(["kpis", "progress"]);
      await queryClient.invalidateQueries(["kpis", "targets"]);
    },
    onError: (err: Error): void => {
      console.info(err);

      addSnackbarToQueue({
        message: "Failed to delete KPI.",
        severity: "error"
      });
    }
  });

  const handleEdit = (kpiId: number): void => {
    setShowEditAnnualKpiDialog(true);
    const { id, kpi_type, activity_type } = annualKpis?.results.find(
      item => item.id === kpiId
    )!;
    setAnnualKpiToEdit({ id, kpiType: kpi_type, activityType: activity_type });
  };

  const handleDelete = (
    id: number,
    kpiType: KpiType,
    activityType: ActivityType
  ): void => {
    setShowDeleteAnnualKpiDialog(true);
    setAnnualKpiToDelete({
      id,
      kpiType,
      activityType
    });
  };

  const handleOpenCategoriesDialog = (): void => {
    setShowCategoriesDialog(true);
  };

  const getCategoryById = useCallback(
    (id: number): Category | undefined => {
      const defaultCategory: Category = {
        id: -1,
        name: "None",
        description: ""
      };

      return (
        categories?.results.find(item => item.id === id) ?? defaultCategory
      );
    },
    [categories]
  );

  return (
    <>
      <Stack
        sx={{
          minHeight: "calc(100vh - 100px)",
          maxHeight: "calc(100vh - 100px)"
        }}
      >
        <>
          <PaddedPaper sx={{ flexGrow: "1", minHeight: "0" }}>
            <Stack spacing={3} minHeight={0}>
              <Stack
                direction="row"
                gap={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack direction="row" gap={2} alignItems="center">
                  <Typography variant="h4">Manage KPIs</Typography>

                  <LinkButton to="/pages/kpis" color="primary">
                    <ChevronLeftIcon /> View KPIs
                  </LinkButton>
                </Stack>

                <ButtonMenu
                  buttonContent={<MoreVertIcon />}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right"
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right"
                  }}
                >
                  <MenuItem onClick={() => setShowCreateAnnualKpiDialog(true)}>
                    Add a new KPI
                  </MenuItem>
                  <MenuItem onClick={handleOpenCategoriesDialog}>
                    Manage Categories
                  </MenuItem>
                </ButtonMenu>
              </Stack>

              <Stack direction="row" gap={2}>
                <FormControl sx={{ width: "300px" }}>
                  <InputLabel id="select-year-label">Year</InputLabel>
                  <Select
                    data-qaid="KPI-Select-Year"
                    labelId="select-year-label"
                    id="select-year"
                    label="Year"
                    value={String(selectedYear) ?? ""}
                    disabled={
                      isYearsUniqueLoading ||
                      isYearsUniqueError ||
                      !yearsUnique?.length
                    }
                    onChange={(event: SelectChangeEvent) => {
                      setSelectedYear(event.target.value);
                    }}
                    endAdornment={
                      <IconButton
                        sx={{ position: "absolute", right: "28px" }}
                        onClick={() => {
                          setSelectedYear("");
                        }}
                      >
                        <CloseRounded />
                      </IconButton>
                    }
                  >
                    {yearsUnique?.map(year => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ width: "300px" }}>
                  <InputLabel id="select-category-label">Category</InputLabel>
                  <Select
                    data-qaid="KPI-Select-Category"
                    labelId="select-category-label"
                    id="select-category"
                    label="Category"
                    value={String(selectedCategory) ?? ""}
                    disabled={
                      isCategoriesLoading ||
                      isCategoriesError ||
                      !categories?.results.length
                    }
                    onChange={(event: SelectChangeEvent) => {
                      setSelectedCategory(event.target.value);
                    }}
                    endAdornment={
                      <IconButton
                        sx={{ position: "absolute", right: "28px" }}
                        onClick={() => {
                          setSelectedCategory("");
                        }}
                      >
                        <CloseRounded />
                      </IconButton>
                    }
                  >
                    {categories?.results.map(category => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              <CardsList
                sx={{ minHeight: "0", overflow: "auto", py: "4px", px: "2px" }}
              >
                {!isKpisLoading &&
                  !isKpisError &&
                  annualKpis.results.map(item => {
                    const siteId = `sitecard-${item.id}`;
                    const category = getCategoryById(item.category)?.name;
                    const displayName = item.workplan_name
                      ? `${item.name} (${item.workplan_name})`
                      : item.name;

                    return (
                      <Card key={siteId} elevation={2}>
                        <CardHeader
                          sx={{
                            pb: 0,
                            minWidth: 0,
                            "& .MuiCardHeader-content": { minWidth: 0 }
                          }}
                          title={
                            <Stack
                              direction="column"
                              gap={0.5}
                              alignItems="start"
                              justifyContent="space-between"
                            >
                              <Tooltip
                                title={displayName}
                                placement="bottom-start"
                              >
                                <Box
                                  sx={{
                                    display: "-webkit-box",
                                    "-webkitLineClamp": "2",
                                    "-webkitBoxOrient": "vertical",
                                    overflow: "hidden"
                                  }}
                                >
                                  {displayName}
                                </Box>
                              </Tooltip>
                              <Tooltip
                                title={category}
                                placement="bottom-start"
                              >
                                <Chip
                                  sx={{
                                    minWidth: 0
                                  }}
                                  size="small"
                                  label={category}
                                />
                              </Tooltip>
                            </Stack>
                          }
                          action={
                            <ButtonMenu
                              buttonContent={<MoreVertIcon />}
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right"
                              }}
                              transformOrigin={{
                                vertical: "top",
                                horizontal: "right"
                              }}
                            >
                              <MenuItem onClick={() => handleEdit(item.id)}>
                                Edit
                              </MenuItem>
                              <MenuItem
                                onClick={() =>
                                  handleDelete(
                                    item.id,
                                    item.kpi_type,
                                    item.activity_type
                                  )
                                }
                              >
                                Delete
                              </MenuItem>
                            </ButtonMenu>
                          }
                        />
                        <CardContent sx={{ py: 0 }}>
                          <Stack>
                            <Box>
                              <KpisLinearProgress
                                variant="determinate"
                                value={item.progress.percentage_metric}
                                sx={{
                                  flexGrow: 1,
                                  flexShrink: 1,
                                  width: "200px"
                                }}
                              />

                              <Tooltip
                                title={item.description}
                                placement="bottom-start"
                              >
                                <Typography
                                  sx={{
                                    display: "-webkit-box",
                                    "-webkitLineClamp": "2",
                                    "-webkitBoxOrient": "vertical",
                                    overflow: "hidden"
                                  }}
                                >
                                  {item.description || "—"}
                                </Typography>
                              </Tooltip>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  })}
                {isLoaded && (
                  <Paper elevation={2} sx={{ display: "flex" }}>
                    <ButtonBase
                      onClick={() => setShowCreateAnnualKpiDialog(true)}
                      sx={{ flexGrow: 1 }}
                      focusRipple
                    >
                      <Stack alignItems="center">
                        <AddCircle
                          color="primary"
                          sx={{ fontSize: "4rem", color: "grey.300" }}
                        />
                        <Typography variant="h5">Add a new KPI</Typography>
                      </Stack>
                    </ButtonBase>
                  </Paper>
                )}
              </CardsList>
            </Stack>
          </PaddedPaper>
        </>
      </Stack>

      {showCreateAnnualKpiDialog && (
        <CreateAnnualKpiDialog
          isOpen={showCreateAnnualKpiDialog}
          onClose={() => setShowCreateAnnualKpiDialog(false)}
          onConfirm={() => {}}
        />
      )}

      {annualKpiToEdit && showEditAnnualKpiDialog && (
        <EditAnnualKpiDialog
          isOpen={showEditAnnualKpiDialog}
          onClose={() => setShowEditAnnualKpiDialog(false)}
          onConfirm={() => {}}
          kpi={{
            id: annualKpiToEdit.id,
            kpiType: annualKpiToEdit.kpiType,
            activityType: annualKpiToEdit.activityType
          }}
        />
      )}

      {showDeleteAnnualKpiDialog && annualKpiToDelete && (
        <ConfirmationDialog
          title="Are you sure you want to delete this KPI?"
          open={showDeleteAnnualKpiDialog}
          onClose={() => setShowDeleteAnnualKpiDialog(false)}
          onConfirm={() => {
            if (Object.keys(annualKpiToDelete).length) {
              const { id, kpiType, activityType } = annualKpiToDelete;
              deleteAnnualKpiMutation.mutate({ id, kpiType, activityType });
              setAnnualKpiToDelete(null);
            }
          }}
        />
      )}

      {showCategoriesDialog && (
        <ListCategoriesDialog
          isOpen={showCategoriesDialog}
          onClose={() => setShowCategoriesDialog(false)}
        />
      )}
    </>
  );
};

const NoDataTextContainer = styled("div")`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  font-size: 24px;
  font-weight: 600;
`;
NoDataTextContainer.displayName = "NoDataTextContainer";

const PaddedPaper = styled(Paper)`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing(2)};
`;
PaddedPaper.displayName = "PaddedPaper";

const CardsList = styled(Box)`
  --height: 200px;
  display: grid;
  gap: ${({ theme }) => theme.spacing(2)};
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  grid-auto-rows: var(--height);
  grid-auto-flow: dense;
  grid-template-rows: repeat(auto-fill, minmax(var(--height), auto));
`;
CardsList.displayName = "CardsList";
